import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { sized } from '@/lib/imageSize';

const API = process.env.REACT_APP_BACKEND_URL;

const SITE_DEFAULT_IMAGE = {
  auto: '/og-cars.jpg',
  coupon: '/og-metromandi.jpg',
  finance: '/og-finance.jpg',
  dgtg: '/og-dgtg.jpg',
  games: '/og-games.jpg',
  vikas: '/og-vikas.jpg',
  devmob: '/og-devmob.jpg',
  travel: '/og-woketraveler.jpg',
  tech: '/og-tech-n-ai-talks.jpg',
};

// basePath must match the site's actual registered blog route (e.g. '/auto/blog',
// '/finance/blog', '/coupon/blog') — a bare '/blog/:slug' is NOT a valid route on
// every site, which previously made these cards silently fail to open.
// textOnly: no thumbnails (finance and auto, where a picture is often wrong and the title says it all).
export default function LatestArticles({ site = '', limit = 6, title = 'Latest Articles', basePath = '', textOnly = false }) {
  const [posts, setPosts] = useState([]);
  const base = basePath || (site ? `/${site}/blog` : '/blog');

  useEffect(() => {
    const params = new URLSearchParams({ limit: String(limit), status: 'published', lightweight: 'true' });
    if (site) params.set('site', site);
    fetch(`${API}/api/content/articles?${params}`)
      .then(r => r.json())
      .then(d => setPosts((d.articles || []).slice(0, limit)))
      .catch(() => {});
  }, [site, limit]);

  if (!posts.length) return null;

  return (
    <section className="bg-gray-50 border-b" data-testid="latest-articles-widget">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{title}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map(post => (
            <Link
              key={post.id}
              to={`${base}/${post.slug || post.id}`}
              className="group flex items-center gap-3 bg-white rounded-lg p-2.5 border border-gray-100 hover:border-gray-300 hover:shadow-sm transition-all"
              data-testid={`article-${post.slug || post.id}`}
            >
              {!textOnly && <div className="w-20 h-16 flex-shrink-0 rounded-md overflow-hidden bg-gray-100">
                <img
                  src={sized(post.featured_image, 320) || SITE_DEFAULT_IMAGE[site] || '/og-default.jpg'}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = SITE_DEFAULT_IMAGE[site] || '/og-default.jpg'; }}
                />
              </div>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 line-clamp-2 group-hover:text-gray-900">{post.title}</p>
                {post.excerpt && <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{post.excerpt}</p>}
              </div>
              <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-gray-400 group-hover:text-emerald-500 transition-colors" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
